/**
 * Estado de servidor das contagens regressivas.
 *
 * Sem escrita otimista, pelo mesmo motivo das listas: sao poucos registros,
 * mudam raramente e nenhuma acao aqui e de alta frequencia. Otimismo custaria
 * complexidade sem economizar espera perceptivel.
 *
 * A excecao e a EDICAO, que passa pela fila offline: sem rede, a mudanca so
 * chega ao servidor na drenagem, e sem o patch no cache a tela mostraria a
 * data antiga ate la — como se a edicao tivesse se perdido.
 */

import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  uuidv7,
  type Countdown,
  type CreateCountdownPayload,
  type UpdateCountdownPayload,
} from '@cleartask/shared';
import { api } from '../../lib/api.js';
import { withOfflineQueue } from '../../lib/sync.js';

export const countdownKeys = {
  all: ['countdowns'] as const,
};

export function useCountdowns() {
  return useQuery({
    queryKey: countdownKeys.all,
    queryFn: () => api.countdowns.list(),
    staleTime: 5 * 60_000,
  });
}

export function newCountdownId(): string {
  return uuidv7();
}

function useInvalidateCountdowns(): () => void {
  const qc = useQueryClient();
  return useCallback(() => {
    void qc.invalidateQueries({ queryKey: countdownKeys.all });
  }, [qc]);
}

export function useCreateCountdown() {
  const invalidate = useInvalidateCountdowns();

  return useMutation({
    mutationFn: (payload: CreateCountdownPayload) => api.countdowns.create(payload),
    onSuccess: invalidate,
  });
}

export function useUpdateCountdown() {
  const qc = useQueryClient();
  const invalidate = useInvalidateCountdowns();

  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: UpdateCountdownPayload }) =>
      withOfflineQueue(
        { entity: 'countdown', action: 'update', entityId: id, payload: patch },
        () => api.countdowns.update(id, patch),
      ),

    onMutate: async ({ id, patch }) => {
      await qc.cancelQueries({ queryKey: countdownKeys.all });
      const anterior = qc.getQueryData<{ countdowns: Countdown[] }>(countdownKeys.all);
      if (anterior) {
        qc.setQueryData<{ countdowns: Countdown[] }>(countdownKeys.all, {
          ...anterior,
          countdowns: anterior.countdowns.map((c) =>
            c.id === id ? { ...c, ...(patch as Partial<Countdown>) } : c,
          ),
        });
      }
      return { anterior };
    },

    onError: (_erro, _variaveis, context) => {
      if (context?.anterior) qc.setQueryData(countdownKeys.all, context.anterior);
    },

    onSettled: invalidate,
  });
}

/**
 * Remove e devolve o registro.
 *
 * Quem chama guarda a resposta para oferecer "Desfazer": nao existe endpoint de
 * undo, e a volta e recriar com o MESMO id (spec 10.3).
 */
export function useDeleteCountdown() {
  const invalidate = useInvalidateCountdowns();

  return useMutation({
    mutationFn: (id: string) => api.countdowns.remove(id),
    onSuccess: invalidate,
  });
}
